/**
 * Table
 *
 * @author Dominique Rau [domi.github@gmail.com](mailto:domi.github@gmail.com)<br/>
 * @version 0.0.1
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Button, makeStyles, TextField, Chip, InputAdornment, } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { yoga } from '@thingos/react-utils-yoga';
import clsx from 'clsx';
import * as React from 'react';
import SearchIcon from '@material-ui/icons/Search';
// body
// ------------------------------
var useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
});
export var Table = function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    var classes = useStyles(props);
    return React.createElement("div", __assign({ className: classes.root }, props));
};
// header
// ------------------------------
var useTableHeaderStyle = makeStyles(function (theme) { return ({
    root: {
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
        padding: '10px 64px',
        position: 'relative',
        borderBottom: 'rgba(0,0,0,0.15) 1px solid',
        flexShrink: 0,
        color: theme.palette.text.primary,
    },
    row: yoga.utils.row.center,
    inline: {
        padding: '10px 68px',
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
        zIndex: 5,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        boxShadow: 'none',
    },
}); });
export var TableHeader = function (props) {
    var classes = useTableHeaderStyle(props);
    return (React.createElement("div", __assign({ className: clsx(classes.row, classes.root, props.inline && classes.inline) }, props)));
};
export var TableHeaderCell = function (_a) {
    var restProps = __rest(_a, []);
    return React.createElement("div", __assign({}, restProps));
};
export var TableHeaderButton = function (props) {
    return React.createElement(Button, __assign({ variant: "outlined" }, props));
};
export var TableHeaderInput = function (props) {
    return React.createElement(TextField, __assign({ margin: "dense", variant: "outlined" }, props));
};
export var TableHeaderAutocomplete = function (props) {
    var filterSuggestions = props.filterSuggestions, filterValue = props.filterValue, onChangeFilter = props.onChangeFilter, restProps = __rest(props, ["filterSuggestions", "filterValue", "onChangeFilter"]);
    var suggestions = filterSuggestions || [];
    return (React.createElement(Autocomplete, __assign({ id: "filter", options: suggestions, value: filterValue, size: "small", freeSolo: true, onChange: function (_, value) { return onChangeFilter === null || onChangeFilter === void 0 ? void 0 : onChangeFilter(value || ''); }, renderInput: function (params) { return (React.createElement(TextField, __assign({}, params, { InputProps: __assign(__assign({}, params.InputProps), { fullWidth: true, startAdornment: (React.createElement(InputAdornment, { position: "start", style: { marginLeft: 4 } },
                    React.createElement(SearchIcon, { color: "disabled" }))) }), size: "small", variant: "outlined", fullWidth: true }))); } }, restProps)));
};
// body
// ------------------------------
var useTableBodyStyle = makeStyles(function (theme) { return ({
    root: {
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100],
        padding: '10px 64px',
        flex: 1,
        overflowY: 'auto',
    },
    column: yoga.column,
}); });
export var TableBody = function (props) {
    var classes = useTableBodyStyle(props);
    return React.createElement("div", __assign({ className: clsx(classes.column, classes.root) }, props));
};
export var useTableRowStyle = makeStyles(function (theme) { return ({
    root: {
        padding: 8,
        marginLeft: -8,
        marginRight: -8,
        marginBottom: 10,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 3px 6px 0 rgba(0,0,0,0.10)',
        borderRadius: 5,
        overflow: 'hidden',
        flexShrink: 0,
    },
    filter: {
        padding: 8,
        marginLeft: -8,
        marginRight: -8,
        marginBottom: 10,
        overflow: 'hidden',
        flexShrink: 0,
    },
    filterItem: {
        marginRight: 8,
        marginBottom: 8,
    },
    row: __assign({}, yoga.utils.row.center),
}); });
export var TableFilterRow = function (props) {
    var classes = useTableRowStyle(props);
    var activeFilters = props.activeFilters, onFilterRemove = props.onFilterRemove, restProps = __rest(props, ["activeFilters", "onFilterRemove"]);
    if ((activeFilters === null || activeFilters === void 0 ? void 0 : activeFilters.length) === 0)
        return null;
    return (React.createElement("div", __assign({ className: clsx(classes.filter) }, restProps), activeFilters.map(function (f, idx) {
        return (React.createElement(Chip, { className: classes.filterItem, label: f, onDelete: function () {
                onFilterRemove === null || onFilterRemove === void 0 ? void 0 : onFilterRemove(f, idx);
            } }));
    })));
};
export var TableRow = function (props) {
    var classes = useTableRowStyle(props);
    return React.createElement("div", __assign({ className: clsx(classes.row, classes.root) }, props));
};
var useTableRowCellStyle = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        flexGrow: 0,
        overflow: 'hidden',
    },
});
export var TableRowCell = function (_a) {
    var style = _a.style, restProps = __rest(_a, ["style"]);
    var classes = useTableRowCellStyle(restProps);
    return React.createElement("div", __assign({ className: classes.root }, restProps, { style: __assign({}, style) }));
};
